import { useTranslation } from 'react-i18next'
import { PageTitle } from '../components/common/PageTitle/PageTitle'
import { RoleRestrictedContent } from '@/components/molecules/RoleRestrictedContent/RoleRestrictedContent'
import { UserType } from '@/hooks'
import { Outlet } from '@tanstack/react-router'
import { useSustainabilityHeaderSlot } from '@/features/Sustainability/components/SustainabilityHeaderSlot/useSustainabilityHeaderSlot'
import { ReportHeader } from '@/features/Reporting/components/ReportHeader/ReportHeader'
import { SustainabilityFilters } from '@/features/Sustainability/components/SustainabilityFilters/SustainabilityFilters'
import { Badge } from '@/components/common/Badge/Badge'
import { PremiumOverviewContainer } from '@/features/Sustainability/components/PremiumOverviewContainer/PremiumOverviewContainer'
import { ErrorState, LoadingState } from '@/components'
import { ReportRestrictedContent } from './ReportRestrictedContent'
import { SustainabilityTabs } from '@/features/Sustainability/components/SustainabilityTabs/SustainabilityTabs'
import { SustainabilityHeaderSlotProvider } from '@/features/Sustainability/components/SustainabilityHeaderSlot/SustainabilityHeaderSlotProvider'
import { FilterError } from '@/components/common/FilterError/FilterError'
import { useNodesCommoditiesFiltersError } from '@/hooks/useNodesCommoditiesFiltersError/useNodesCommoditiesFiltersError'
import { RoleBasedPlaceholder } from '@/components/organisms/RoleBasedPlaceholder/RoleBasedPlaceholder'
import { useReportAccess } from '@/hooks/useReportAccess'

export const Sustainability = () => {
    const { t } = useTranslation('translations')
    const { slot } = useSustainabilityHeaderSlot()
    const title = t('sidebar_menu.sustainability')

    const {
        isLoading,
        isFetching,
        isFetched,
        isError,
        error,
        isAgentOrBrokerWithoutCustomer,
        shouldShowRestrictedContent: shouldShowPremiumOverview,
    } = useReportAccess()
    const { isFiltersError } = useNodesCommoditiesFiltersError()

    if (isError) {
        if (
            error &&
            'status' in error &&
            typeof error.status === 'number' &&
            403 === error.status
        ) {
            return (
                <div className="grid h-full px-6 pb-6">
                    <ReportRestrictedContent />
                </div>
            )
        }

        return (
            <ErrorState
                className="h-full"
                description={t('common.error_state.description')}
                boxed={false}
            />
        )
    }
    if (isAgentOrBrokerWithoutCustomer) {
        return (
            <div className="grid h-full grow grid-cols-[100%] grid-rows-[auto_1fr]">
                <PageTitle
                    label={title}
                    labelTestId="page-title-sustainability"
                    className="!pr-2"
                />

                <RoleBasedPlaceholder />
            </div>
        )
    }
    if (isLoading || isFetching || !isFetched) {
        return <LoadingState message={t('common.loading')} size="small" />
    }

    if (shouldShowPremiumOverview) {
        return <PremiumOverviewContainer />
    }

    return (
        <div className="grid h-full grow grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="space-y-6">
                <ReportHeader
                    title={title}
                    renderTitle={() => (
                        <div className="flex items-center">
                            <PageTitle
                                label={title}
                                labelTestId="page-title-sustainability"
                                className="!pr-2"
                            />

                            <RoleRestrictedContent
                                allowedRoles={[UserType.CUSTOMER]}
                                strict={false}
                            >
                                <Badge
                                    icon="star"
                                    iconSize="small"
                                    text={t('common.premium')}
                                    iconClassName="material-symbols-rounded-filled"
                                />
                            </RoleRestrictedContent>
                        </div>
                    )}
                >
                    {slot}
                </ReportHeader>

                <RoleRestrictedContent
                    allowedRoles={[UserType.CUSTOMER]}
                    strict={false}
                >
                    <SustainabilityTabs />
                    {isFiltersError ? (
                        <FilterError />
                    ) : (
                        <SustainabilityFilters />
                    )}
                </RoleRestrictedContent>
            </div>
            {!isFiltersError && <Outlet />}
        </div>
    )
}

const SustainabilityWithContext = () => {
    return (
        <SustainabilityHeaderSlotProvider>
            <Sustainability />
        </SustainabilityHeaderSlotProvider>
    )
}

export default SustainabilityWithContext
